import {
  DEFAULT_LOGO_IMAGE,
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  SITE_TAGLINE,
  absoluteRouteUrl,
  toAbsoluteUrl,
} from "./site";

export const defaultKeywords = [
  "produk herbal",
  "teh rosella",
  "teh bunga telang",
  "vitamin herbal",
  "suplemen alami",
  "minuman herbal alami",
  "brand herbal Indonesia",
  "Teras Alami",
];

export const buildMeta = ({
  title,
  description,
  path = "/",
  type = "website",
  image = DEFAULT_OG_IMAGE,
  keywords = defaultKeywords,
  noIndex = false,
}) => {
  const canonical = absoluteRouteUrl(path);
  const absoluteImage = toAbsoluteUrl(image);

  return {
    title: title
      ? title.includes(SITE_NAME)
        ? title
        : `${title} | ${SITE_NAME}`
      : `${SITE_NAME} | ${SITE_TAGLINE}`,
    description,
    canonical,
    image: absoluteImage,
    type,
    keywords,
    robots: noIndex ? "noindex, nofollow, noarchive" : "index, follow, max-image-preview:large",
  };
};

export const buildBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: absoluteRouteUrl(item.path),
  })),
});

export const buildFaqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.a,
    },
  })),
});

export const buildOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  alternateName: "Teras Alami Herbal & Wellness",
  url: absoluteRouteUrl("/"),
  logo: toAbsoluteUrl(DEFAULT_LOGO_IMAGE),
  image: toAbsoluteUrl(DEFAULT_OG_IMAGE),
  brand: SITE_NAME,
  description:
    "Teras Alami adalah brand herbal Indonesia yang menghadirkan minuman herbal alami, teh rosella, teh bunga telang, dan pilihan wellness untuk gaya hidup sehat modern.",
  areaServed: "ID",
  sameAs: [],
});

export const buildWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: absoluteRouteUrl("/"),
  inLanguage: "id-ID",
  description:
    "Website resmi Teras Alami untuk produk herbal alami, teh rosella, teh bunga telang, dan edukasi gaya hidup sehat keluarga.",
});

const normalizeProductAvailability = (product) => {
  const active = product.is_active ?? product.active ?? true;
  const stock = Number(product.stock ?? 0);
  return active && stock > 0
    ? "https://schema.org/InStock"
    : "https://schema.org/OutOfStock";
};

export const buildProductSchema = (product, overrides = {}) => {
  const name = overrides.name || product.name;
  const description =
    overrides.description || product.description || product.benefit || "";

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    brand: {
      "@type": "Brand",
      name: SITE_NAME,
    },
    image: [toAbsoluteUrl(overrides.image || product.image || DEFAULT_OG_IMAGE)],
    description,
    category: product.category || "Produk Herbal",
    sku: product.id,
    url: absoluteRouteUrl(overrides.path || "/"),
    offers: {
      "@type": "Offer",
      priceCurrency: "IDR",
      price: String(product.price ?? 0),
      availability: normalizeProductAvailability(product),
      url: absoluteRouteUrl(overrides.path || "/"),
      itemCondition: "https://schema.org/NewCondition",
    },
    additionalProperty: [
      {
        "@type": "PropertyValue",
        name: "Brand",
        value: SITE_NAME,
      },
    ],
  };
};

export const buildCollectionPageSchema = ({
  name,
  description,
  path,
  products = [],
}) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name,
  description,
  url: absoluteRouteUrl(path),
  inLanguage: "id-ID",
  mainEntity: products.map((product) => buildProductSchema(product, { path })),
});
